import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from '../components/Navbar';
import { MapboxMap } from '../components/MapboxMap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  ChatCircleDots, 
  X, 
  PaperPlaneRight, 
  Sparkle,
  MapPin,
  Leaf,
  Lightning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InteractiveMapPage = () => {
  const { token } = useAuth();
  const [map, setMap] = useState(null);
  const [baseStyle, setBaseStyle] = useState('dark');
  const [features, setFeatures] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const sessionId = useRef(`session-${Date.now()}`).current;
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchFeatures();
  }, []);

  useEffect(() => {
    if (map && features) {
      addFeaturesToMap();
    }
  }, [map, features]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchFeatures = async () => {
    try {
      const response = await axios.get(`${API}/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch features:', error);
      toast.error('Failed to load map features');
    }
  };

  const addFeaturesToMap = () => {
    if (!map || !features) return;

    // Wait for map style to load
    if (!map.isStyleLoaded()) {
      map.once('style.load', addFeaturesToMap);
      return;
    }

    // Remove existing layers and source if they exist
    if (map.getLayer('features-points')) map.removeLayer('features-points');
    if (map.getLayer('features-polygons')) map.removeLayer('features-polygons');
    if (map.getSource('features')) map.removeSource('features');

    // Add source
    map.addSource('features', {
      type: 'geojson',
      data: features
    });

    // Add point layer
    map.addLayer({
      id: 'features-points',
      type: 'circle',
      source: 'features',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 8,
        'circle-color': [
          'match',
          ['get', 'suite', ['get', 'properties']],
          'TerraVeritas', '#22C55E',
          'UrbIntellect', '#3B82F6',
          'AgriGrow', '#F5A623',
          'GeoCore', '#FFFFFF',
          '#10B981'
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF'
      }
    });

    // Add polygon layer
    map.addLayer({
      id: 'features-polygons',
      type: 'fill',
      source: 'features',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': [
          'match',
          ['get', 'suite', ['get', 'properties']],
          'TerraVeritas', '#22C55E',
          'UrbIntellect', '#3B82F6',
          'AgriGrow', '#F5A623',
          'GeoCore', '#FFFFFF',
          '#10B981'
        ],
        'fill-opacity': 0.3
      }
    });

    // Add click handlers for popups
    map.on('click', 'features-points', (e) => {
      const properties = e.features[0].properties;
      new window.mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="color: #000; padding: 8px;">
            <h3 style="font-weight: bold; margin-bottom: 4px;">${properties.name || 'Feature'}</h3>
            <p style="font-size: 12px; color: #666;">Suite: ${properties.suite || 'N/A'}</p>
            <p style="font-size: 12px; color: #666;">Value: ${properties.value || 'N/A'}</p>
            <p style="font-size: 12px; color: #666;">Status: ${properties.status || 'N/A'}</p>
          </div>
        `)
        .addTo(map);
    });

    // Change cursor on hover
    map.on('mouseenter', 'features-points', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'features-points', () => {
      map.getCanvas().style.cursor = '';
    });
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        {
          session_id: sessionId,
          message: chatInput
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      const errorMessage = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#050505]" data-testid="map-page">
      <Navbar />
      
      <div className="flex-1 relative">
        {/* Map */}
        <MapboxMap 
          onMapReady={setMap} 
          baseStyle={baseStyle}
          features={features}
        />

        {/* Map Controls */}
        <div className="absolute top-4 left-4 glass-panel rounded-sm p-4 space-y-3" data-testid="map-controls">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Base Map</p>
            <div className="flex gap-2">
              <button
                onClick={() => setBaseStyle('dark')}
                className={`px-3 py-1 rounded-sm text-xs transition-colors ${
                  baseStyle === 'dark'
                    ? 'bg-emerald-500 text-black font-bold'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                data-testid="style-dark"
              >
                Dark
              </button>
              <button
                onClick={() => setBaseStyle('satellite')}
                className={`px-3 py-1 rounded-sm text-xs transition-colors ${
                  baseStyle === 'satellite'
                    ? 'bg-emerald-500 text-black font-bold'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                data-testid="style-satellite"
              >
                Satellite
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Legend</p>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#22C55E]"></div>
                <span className="text-xs text-white">TerraVeritas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#3B82F6]"></div>
                <span className="text-xs text-white">UrbIntellect</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#F5A623]"></div>
                <span className="text-xs text-white">AgriGrow</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-white"></div>
                <span className="text-xs text-white">GeoCore</span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Chat Widget */}
        {!chatOpen && (
          <button
            onClick={() => setChatOpen(true)}
            className="absolute bottom-4 right-4 p-4 bg-emerald-500 hover:bg-emerald-600 rounded-full shadow-lg transition-all duration-200 pulse-glow"
            data-testid="chat-toggle-button"
          >
            <ChatCircleDots size={28} weight="bold" className="text-black" />
          </button>
        )}

        {chatOpen && (
          <div className="absolute bottom-4 right-4 w-[400px] h-[600px] flex flex-col neon-border rounded-sm bg-black" data-testid="chat-panel">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-emerald-500/30">
              <div className="flex items-center gap-2">
                <Sparkle size={20} weight="fill" className="text-emerald-500" />
                <h3 className="font-bold text-white">GeoAI Assistant</h3>
              </div>
              <button
                onClick={() => setChatOpen(false)}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                data-testid="chat-close-button"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkle size={48} weight="duotone" className="text-emerald-500/30 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Ask me about geospatial data, SDG metrics, or environmental insights!</p>
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-sm text-sm ${
                      msg.role === 'user'
                        ? 'bg-emerald-500 text-black'
                        : 'bg-white/10 text-white'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/10 text-white p-3 rounded-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-emerald-500/30">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask GeoAI..."
                  className="flex-1 bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-emerald-500 focus:outline-none"
                  disabled={chatLoading}
                  data-testid="chat-input"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={chatLoading || !chatInput.trim()}
                  className="p-2 bg-emerald-500 hover:bg-emerald-600 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chat-send-button"
                >
                  <PaperPlaneRight size={20} weight="bold" className="text-black" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
